import { useFormikContext } from 'formik';

import { FormSection } from '@@/form-components/FormSection';
import { FormError } from '@@/form-components/FormError';
import { InputList } from '@@/form-components/InputList';
import { ExternalLink } from '@@/ExternalLink';

import { NamespaceEntryItem } from './NamespaceEntryItem';
import {
  buildNamespaceEntry,
  PodSecurityNamespaceEntry,
  PodSecurityStandardsFormValues,
} from './types';

const POD_SECURITY_STANDARDS_DOCS_URL =
  'https://kubernetes.io/docs/concepts/security/pod-security-standards/';

export function PodSecurityStandardsFormFields() {
  const { values, setFieldValue, errors } =
    useFormikContext<PodSecurityStandardsFormValues>();

  const listError =
    typeof errors.namespaces === 'string' ? errors.namespaces : undefined;
  const entryErrors = Array.isArray(errors.namespaces)
    ? errors.namespaces
    : undefined;

  return (
    <>
      <FormSection
        title="About Pod Security Standards"
        isFoldable
        defaultFolded={false}
      >
        <p className="text-muted text-sm">
          Each namespace can apply a Pod Security Standards profile, from least
          to most restrictive:
        </p>
        <ul className="text-muted ml-5 list-disc text-sm">
          <li>
            <strong>privileged</strong> — unrestricted; allows known privilege
            escalations.
          </li>
          <li>
            <strong>baseline</strong> — prevents known privilege escalations
            while allowing default pod configurations.
          </li>
          <li>
            <strong>restricted</strong> — heavily restricted, following current
            pod hardening best practices.
          </li>
        </ul>
        <p className="text-muted mt-2 text-sm">
          Choose a profile per mode. The mode sets how the cluster reacts when a
          pod violates that profile: <strong>enforce</strong> blocks the pod,{' '}
          <strong>audit</strong> records the violation in the audit log, and{' '}
          <strong>warn</strong> returns a warning to the user. Leave a mode
          unset to leave it unmanaged.{' '}
          <ExternalLink
            to={POD_SECURITY_STANDARDS_DOCS_URL}
            data-cy="pss-docs-link"
          >
            Learn more
          </ExternalLink>
        </p>

        <p className="text-muted mt-3 text-sm font-medium">
          Recommended approach
        </p>
        <ul className="text-muted ml-5 list-disc text-sm">
          <li>
            Sensitive production namespaces:{' '}
            <strong>enforce = restricted</strong>
          </li>
          <li>
            Testing and legacy namespaces: <strong>enforce = baseline</strong>,{' '}
            <strong>warn = restricted</strong>
          </li>
          <li>
            Trusted infrastructure namespaces:{' '}
            <strong>enforce = privileged</strong> or <strong>baseline</strong>
          </li>
        </ul>

        <p className="text-muted text-sm">
          System namespaces (kube-system, kube-public, kube-node-lease) and the
          Portainer namespaces (portainer, portainer-agent) must use the
          privileged profile.
        </p>
      </FormSection>

      <FormSection title="Namespaces">
        <InputList<PodSecurityNamespaceEntry>
          value={values.namespaces}
          onChange={(namespaces) => setFieldValue('namespaces', namespaces)}
          item={NamespaceEntryItem}
          itemBuilder={buildNamespaceEntry}
          errors={entryErrors}
          addLabel="Add namespace"
          data-cy="pss-namespaces-list"
        />

        {listError && <FormError>{listError}</FormError>}
      </FormSection>
    </>
  );
}
